/**
 * Format date string to a human-readable format
 * @param dateStr The date string to format
 * @param options The Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export const formatDate = (
  dateStr: string,
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
): string => {
  try {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr; // Return original string if parsing fails
  }
};

/**
 * Format date string to date only (without time)
 * @param dateStr The date string to format
 * @returns Formatted date string (date only)
 */
export const formatDateOnly = (dateStr: string): string => {
  return formatDate(dateStr, { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Format date string to time only
 * @param dateStr The date string to format
 * @returns Formatted time string
 */
export const formatTime = (dateStr: string): string => {
  return formatDate(dateStr, { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * Get relative time (e.g., "2 hours ago", "just now")
 * @param dateStr The date string to format
 * @returns Relative time string
 */
export const getRelativeTime = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return dateStr; // Return original string if parsing fails
  }
}; 